import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const POSTER = "/media/hands-galaxy-poster.webp";

/** Module scope, so it resets on a full page load (refresh) but survives in-app navigation. */
let playedThisPageLoad = false;

function shouldPlay(): boolean {
  if (playedThisPageLoad || typeof window === "undefined") return false;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return !connection?.saveData;
}

/**
 * The hands-galaxy banner behind the Home hero input. It plays once per page load, muted,
 * then rests on its last frame, which is also the poster, so the still and the end of the
 * clip are the same picture. Coming back to Home inside the app, reduced motion and Save-Data
 * all get the still frame without playing.
 */
export function HandsGalaxyVideo({ className }: { className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [play] = useState(shouldPlay);

  useEffect(() => {
    if (!play) return;
    playedThisPageLoad = true;
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    // Autoplay can be refused (e.g. low-power mode); the poster is the resting frame anyway.
    void video.play().catch(() => {});
  }, [play]);

  if (!play) {
    return <img src={POSTER} alt="" aria-hidden className={cn("object-cover", className)} />;
  }

  return (
    <video
      ref={videoRef}
      aria-hidden
      tabIndex={-1}
      muted
      playsInline
      autoPlay
      preload="auto"
      poster={POSTER}
      disablePictureInPicture
      className={cn("object-cover", className)}
    >
      <source src="/media/hands-galaxy.webm" type="video/webm" />
      <source src="/media/hands-galaxy.mp4" type="video/mp4" />
    </video>
  );
}
